import React from "react";
import { connect } from "react-redux";
import _ from "lodash";
import * as resActions from "../../modules/resources";
import withPagination from "Components/withPagination";
import Loader from "Components/loader/Loader";
import { panelDefaultTitle } from "../../config";
import PropTypes from "prop-types";

import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import Button from "Components/button/Button";
import { alertSuccess } from "Utils/alerts";
import TranslatedString from "Components/TranslatedString";
import { t } from "i18next";
import MainCustomizationRow from "Components/mainCustomizationRow/mainCustomizationRow";
import CustomizationRow from "Components/customizationRow/CustomizationRow";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import { addCustomization, plus } from "Icons/index";
import { withRouter } from "react-router";
import cx from "classnames";
import s from "./List.scss";
import { view, store } from "@risingstack/react-easy-state";
import QboxmailApi from "@/qboxmailApi";
import sessionStore from "@/stores/session";

const propTypes = {};

const ContextTypes = {
  router: PropTypes.object,
  getLoggedUser: PropTypes.func,
  getLoggedUserType: PropTypes.func,
  isLoggedUserAdmin: PropTypes.func,
  isMobileDevice: PropTypes.func,
  isMediaMobile: PropTypes.func,
  isMediaSM: PropTypes.func,
  isMediaMD: PropTypes.func,
  isMediaLG: PropTypes.func,
};

class CustomizationListScreen extends React.Component {
  state = {
    confirmModalOpened: false,
    customizationToDelete: {},
    activeTab: "main",
  };

  // local state
  customizations = store({ list: [], loading: true, pagination: {}, refreshing: false });
  managerCustomizations = store({ list: [], loading: true, pagination: {}, refreshing: false });
  mainCustomization = store({ data: null, loading: true, refreshing: false });
  api = new QboxmailApi({ useCookie: true });

  async componentDidMount() {
    await this.getMainCustomization();

    if (this.context.getLoggedUserType() !== "Manager") {
      await this.getCustomizations();
      await this.getManagersCustomizations();
    }

    // this page has no pagination nor search, so clean the query params
    // if they are present
    if (!this.context.isLoggedUserAdmin() && this.props.location.search) {
      let url = "/customizations";

      if (this.props.location.hash === "#domains" && !IS_APP) {
        url += "#domains";
      }

      this.props.history.replace(url);
    }
  }

  getManagersCustomizations = async (refreshing = false) => {
    let query = {};

    if (this.context.isLoggedUserAdmin()) {
      query.customer_code = this.props.match.params.customerCode;
    }

    if (!refreshing) this.managerCustomizations.loading = true;
    if (refreshing) this.managerCustomizations.refreshing = true;

    let resp;
    try {
      resp = await this.api.getManagersCustomizations(query);

      this.managerCustomizations.list = resp.resources;
      this.managerCustomizations.pagination = resp.pagination;
    } catch (e) {
      if (e.status !== 404) {
        console.log("getCustomizations error", e);
      }
    }

    if (!refreshing) this.managerCustomizations.loading = false;
    if (refreshing) this.managerCustomizations.refreshing = false;
  };

  getCustomizations = async (refreshing = false) => {
    let query = {};

    if (this.context.isLoggedUserAdmin()) {
      query.customer_code = this.props.match.params.customerCode;
    }

    if (!refreshing) this.customizations.loading = true;
    if (refreshing) this.customizations.refreshing = true;

    let resp;
    try {
      resp = await this.api.getCustomizations(query);

      this.customizations.list = resp.resources;
      this.customizations.pagination = resp.pagination;
    } catch (e) {
      if (e.status !== 404) {
        console.log("getCustomizations error", e);
      }
    }

    if (!refreshing) this.customizations.loading = false;
    if (refreshing) this.customizations.refreshing = false;
  };

  getMainCustomization = async () => {
    this.mainCustomization.loading = true;

    let query = {};

    if (this.context.isLoggedUserAdmin()) {
      query.customer_code = this.props.match.params.customerCode;
    }

    let resp;
    try {
      resp = await this.api.getMainCustomization(query);

      this.mainCustomization.data = resp.resources[0];
    } catch (e) {
      if (e.status !== 404) {
        console.log("getMainCustomization error", e);
      }
    }

    this.mainCustomization.loading = false;
  };

  showCustomization = (id) => {
    let url = `/customizations/${id}`;
    if (this.context.isLoggedUserAdmin()) {
      url = `/customers/${this.props.match.params.customerCode}${url}`;
    }

    this.props.history.push({
      pathname: url,
      state: {
        prevPagination: this.props.location.search,
      },
    });
  };

  showMainCustomization = () => {
    let url = `/customizations/main`;
    if (this.context.isLoggedUserAdmin()) {
      url = `/customers/${this.props.match.params.customerCode}${url}`;
    }

    this.props.history.push({
      pathname: url,
      state: {
        prevPagination: this.props.location.search,
      },
    });
  };

  deleteCustomization = async () => {
    const id = this.state.customizationToDelete.id;
    const isMain = this.state.customizationToDelete.is_main;
    if (isMain) {
      sessionStore.resetCustomization();

      // reset the private label logo and title instead of the deleted main customization in the localStorage
      let customUrlLogo = window.localStorage.getItem("qb_customization_custom_url_logo");
      if (customUrlLogo) {
        window.localStorage.setItem("qb_customization_logo", customUrlLogo);
      }

      let customUrlCompany = window.localStorage.getItem("qb_customization_custom_url_company");
      if (customUrlCompany) {
        window.localStorage.setItem("qb_customization_company", customUrlCompany);
      }
    }

    this.setState({ customizationToDelete: undefined, confirmModalOpened: false }, async () => {
      this.customizations.list = this.customizations.list.filter((cus) => cus.id !== id);
      this.managerCustomizations.list = this.managerCustomizations.list.filter((cus) => cus.id !== id);

      if (isMain) this.mainCustomization.data = null;

      alertSuccess(t("alert.customization.deleted"));
      await this.api.deleteCustomization(id);
    });
  };

  goToAddCustomization = (isMain = false) => {
    let url;
    if (isMain) {
      url = "/customizations/main/add";
    } else {
      url = "/customizations/add";
    }

    if (this.context.isLoggedUserAdmin()) {
      url = `/customers/${this.props.match.params.customerCode}${url}`;
    }

    this.props.history.push({
      pathname: url,
      state: {
        prevPagination: this.props.location.search,
      },
    });
  };

  showDeleteConfirmModal = (customization) => {
    this.setState({
      customizationToDelete: customization,
      confirmModalOpened: true,
    });
  };

  closeDeleteConfirmModal = () => {
    this.setState({
      customizationToDelete: undefined,
      confirmModalOpened: false,
    });
  };

  renderManagersHeaders = () => {
    return [
      {
        name: t("table.header.logo"),
        size: "13rem",
      },
      {
        name: t("table.header.managers"),
        size: "13rem",
      },
      { name: t("table.header.company"), size: "18rem" },
      { name: t("table.header.supportURL"), size: "auto", hideOnMobile: true },
      { size: "3rem", center: true },
    ];
  };

  renderMainHeaders = () => {
    return [
      {
        name: t("table.header.logo"),
        size: "13rem",
      },
      { name: t("table.header.company"), size: "18rem" },
      { name: t("table.header.supportURL"), size: "auto", hideOnMobile: true },
      { size: "3rem", center: true },
    ];
  };

  renderDomainsHeaders = () => {
    return [
      { name: t("table.header.logo"), size: "13rem" },
      { name: t("table.header.company"), size: "18rem" },
      { name: t("table.header.domains"), size: "4rem" },
      { name: t("table.header.supportURL"), size: "auto", hideOnMobile: true },
      { size: "3rem", center: true },
    ];
  };

  renderDomainsRows = () => {
    return this.customizations.list.map((c) => {
      return [
        <CustomizationRow
          showCustomization={this.showCustomization}
          showDeleteConfirmModal={this.showDeleteConfirmModal}
          customization={c}
          key={c.id}
        />,
      ];
    });
  };

  renderManagersRows = () => {
    return this.managerCustomizations.list.map((c) => {
      return [
        <MainCustomizationRow
          canEdit={false}
          showDeleteConfirmModal={this.showDeleteConfirmModal}
          mainCustomization={c}
          key={c.id}
          showManager
        />,
      ];
    });
  };

  renderMainRows = () => {
    if (!this.mainCustomization.data) {
      return [];
    }

    return [
      <MainCustomizationRow
        showMainCustomization={this.showMainCustomization}
        showDeleteConfirmModal={this.showDeleteConfirmModal}
        mainCustomization={this.mainCustomization.data}
        key={this.mainCustomization.data.id}
      />,
    ];
  };

  showManagersCustomizationsTab = () => {
    let url = "/customizations";

    if (!IS_APP) {
      url += "#managers";
    }

    if (this.context.isLoggedUserAdmin()) {
      url = `/customers/${this.props.match.params.customerCode}${url}`;
    }

    this.props.history.push(url);
    this.setState({ activeTab: "managers" });
  };

  showDomainsCustomizationsTab = () => {
    let url = "/customizations";

    if (!IS_APP) {
      url += "#domains";
    }

    if (this.context.isLoggedUserAdmin()) {
      url = `/customers/${this.props.match.params.customerCode}${url}`;
    }

    this.props.history.push(url);
    this.setState({ activeTab: "domains" });
  };

  showMainCustomizationTab = () => {
    let url = "/customizations";
    if (this.context.isLoggedUserAdmin()) {
      url = `/customers/${this.props.match.params.customerCode}${url}`;
    }

    this.props.history.push(url);
    this.setState({ activeTab: "main" });
  };

  render() {
    const { customizationToDelete } = this.state;
    const { isMediaMobile, getLoggedUserType } = this.context;

    const mainCustomizationNotExist = !this.mainCustomization.data || !this.mainCustomization.data.id;
    let domainsTabsActive;
    let managersTabsActive;

    if (IS_APP) {
      domainsTabsActive = this.state.activeTab === "domains";
      managersTabsActive = this.state.activeTab === "managers";
    } else {
      domainsTabsActive = this.props.location.hash === "#domains";
      managersTabsActive = this.props.location.hash === "#managers";
    }

    const isManager = getLoggedUserType() === "Manager";

    return (
      <div className="contentWrapper">
        <div className={s.listCustomizations}>
          <div className={s.headerList}>
            <div className={cx("sectionTitle", s.titleList)}>{t("customizations.list.header.title")}</div>

            {!isMediaMobile() && (
              <div className={cx("sectionDesc", s.descList)}>
                <TranslatedString>customizations.list.header.dsc</TranslatedString>
              </div>
            )}
            <div className={s.actionsList}>
              <Button docsKey="customizations" color="docs" className={s.btnList} onlyIcon={isMediaMobile()}>
                button.docs
              </Button>
            </div>
          </div>
          <div className={s.contentList}>
            {!isManager && (
              <div className={s.scrollTabs}>
                <div className={s.tabs}>
                  <div
                    className={cx(s.itemTab, {
                      [s.active]: !domainsTabsActive && !managersTabsActive,
                    })}
                    onClick={this.showMainCustomizationTab}
                  >
                    {t("tab.customizations.general")}
                  </div>

                  <div
                    className={cx(s.itemTab, { [s.active]: domainsTabsActive })}
                    onClick={this.showDomainsCustomizationsTab}
                  >
                    {t("tab.customizations.domains")} [{this.customizations.list.length}]
                  </div>

                  <div
                    className={cx(s.itemTab, { [s.active]: managersTabsActive })}
                    onClick={this.showManagersCustomizationsTab}
                  >
                    {t("tab.customizations.managers")} [{this.managerCustomizations.list.length}]
                  </div>
                </div>
              </div>
            )}
            <div className={s.wrapInfoBtn}>
              {!isMediaMobile() && (
                <div className={cx("sectionDesc")}>
                  {domainsTabsActive ? (
                    <TranslatedString>customizations.show.desc.domains</TranslatedString>
                  ) : managersTabsActive ? (
                    <TranslatedString>customizations.show.desc.managers</TranslatedString>
                  ) : (
                    <TranslatedString>customizations.show.desc.general</TranslatedString>
                  )}
                </div>
              )}

              {(mainCustomizationNotExist || domainsTabsActive) && !managersTabsActive && (
                <Button className={s.btnList} icon={plus} onClick={() => this.goToAddCustomization(!domainsTabsActive)}>
                  button.add.customization
                </Button>
              )}
            </div>

            <div className={s.table}>
              <ResponsiveTable
                history={this.props.history}
                areRowsLoading={
                  domainsTabsActive
                    ? this.customizations.loading
                    : managersTabsActive
                    ? this.managerCustomizations.loading
                    : this.mainCustomization.loading
                }
                location={this.props.location}
                addIcon={addCustomization}
                addMsg={t("table.row.add.msg.customization")}
                addDesc={t("table.row.add.desc.customization")}
                headers={
                  domainsTabsActive
                    ? this.renderDomainsHeaders()
                    : managersTabsActive
                    ? this.renderManagersHeaders()
                    : this.renderMainHeaders()
                }
                rows={
                  domainsTabsActive
                    ? this.renderDomainsRows()
                    : managersTabsActive
                    ? this.renderManagersRows()
                    : this.renderMainRows()
                }
              />
            </div>
          </div>
        </div>

        <ConfirmModal
          title={t("modal.delete.customization.title")}
          text={
            <TranslatedString subs={{ company: customizationToDelete ? customizationToDelete.company : "" }}>
              modal.delete.customization.text
            </TranslatedString>
          }
          textButton={`button.delete`}
          opened={this.state.confirmModalOpened}
          action={this.deleteCustomization}
          handleClose={this.closeDeleteConfirmModal}
        />
      </div>
    );
  }
}

CustomizationListScreen.contextTypes = ContextTypes;

export default withRouter(view(CustomizationListScreen));
